import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  BodyText,
  Button,
  getCloudinaryUrl,
  Image,
  Modal,
  ModalDialogue,
  theme,
  useResponsive,
} from '@sg/garnish';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../../constants';
import { type CustomizationMachineTypes } from '../../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationOutOfStockModal = (props: Props) => {
  const {
    isVisible,
    outOfStockIngredients,
    productName,
    restaurantName,
    singleIngredientBody,
    multipleIngredientsBody,
    singleIngredientButtonTitle,
    multipleIngredientsButtonTitle,
    onRequestClose,
    onChooseNewIngredientPress,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  const isMissingMultipleIngredients = outOfStockIngredients.length > 1;

  const formattedBody = isMissingMultipleIngredients
    ? formatBody({
        body: multipleIngredientsBody,
        productName,
        restaurantName,
        fallback: formatMessage(messages.outOfStockBodyPlural, {
          product: productName,
          restaurant: restaurantName,
        }),
      })
    : formatBody({
        body: singleIngredientBody,
        productName,
        restaurantName,
        fallback: formatMessage(messages.outOfStockBodySingular, {
          product: productName,
          restaurant: restaurantName,
        }),
      });

  const formattedButtonTitle = isMissingMultipleIngredients
    ? (multipleIngredientsButtonTitle ?? formatMessage(messages.outOfStockCtaPlural))
    : (singleIngredientButtonTitle ?? formatMessage(messages.outOfStockCtaSingular));

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleCta = useCallback(() => {
    const firstIngredient = outOfStockIngredients[0];
    const groupId = firstIngredient.modificationGroupId;

    onRequestClose();
    onChooseNewIngredientPress?.(groupId);
  }, [outOfStockIngredients, onRequestClose, onChooseNewIngredientPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      size="medium"
      visible={isVisible}
      accessibilityLabel={formatMessage(messages.outOfStockA11y)}
      onRequestClose={onRequestClose}
    >
      <SafeAreaView edges={match([['bottom'], undefined])}>
        <Modal.BodyText sizeMatch={['22']}>{formattedBody}</Modal.BodyText>

        <Modal.Row style={styles.content}>
          {outOfStockIngredients.map((modification) => {
            const { ingredient } = modification;

            if (!ingredient) return null;

            return <IngredientCard key={ingredient.id} ingredient={ingredient} />;
          })}
        </Modal.Row>

        <Modal.Footer style={styles.footer} withoutTopBorder>
          <Button
            style={styles.button}
            size="large-wide"
            palette="primaryOutline"
            onPress={handleCta}
          >
            {formattedButtonTitle}
          </Button>
        </Modal.Footer>
      </SafeAreaView>
    </ModalDialogue>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const IngredientCard = (props: CardProps) => {
  const { ingredient } = props;

  const ingredientAssetUrl = ingredient.asset.url;
  const ingredientImage = getCloudinaryUrl(ingredientAssetUrl, {
    crop: 'scale',
    width: INGREDIENT_CARD_IMAGE_WIDTH,
  });

  return (
    <View key={ingredient.name} style={styles.ingredient}>
      <Image source={ingredientImage} style={styles.image} />
      <BodyText sizeMatch={['18']}>{ingredient.name}</BodyText>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBody(params: {
  body: string | undefined;
  productName: string | undefined;
  restaurantName: string | undefined;
  fallback: string;
}) {
  const { body, productName = '', restaurantName = '', fallback } = params;

  if (!body) return fallback;

  return body
    .replaceAll(DYNAMIC_PRODUCT_TEXT, productName)
    .replaceAll(DYNAMIC_RESTAURANT_TEXT, restaurantName);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_SIZE = 30;
const DYNAMIC_PRODUCT_TEXT = '(product)';
const DYNAMIC_RESTAURANT_TEXT = '(restaurant)';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  outOfStockA11y: {
    defaultMessage: 'Out of Stock',
    description: 'Customization | Out of Stock | A11y',
  },
  outOfStockBodySingular: {
    defaultMessage:
      'This ingredient is usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'Customization | Out of Stock | Body (Singular)',
  },
  outOfStockBodyPlural: {
    defaultMessage:
      'These ingredients are usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'Customization | Out of Stock | Body (Plural)',
  },
  outOfStockCtaSingular: {
    defaultMessage: 'Choose a new ingredient',
    description: 'Customization | Out of Stock | CTA (Singular)',
  },
  outOfStockCtaPlural: {
    defaultMessage: 'Choose new ingredients',
    description: 'Customization | Out of Stock | CTA (Plural)',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    marginBottom: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  ingredient: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  footer: {
    alignItems: 'center',
  },
  button: {
    paddingHorizontal: theme.spacing['16'],
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: IMAGE_SIZE,
    backgroundColor: theme.colors.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  outOfStockIngredients: CustomizationMachineTypes.ModificationsList;
  productName?: string;
  restaurantName?: string;
  singleIngredientBody?: string;
  multipleIngredientsBody?: string;
  singleIngredientButtonTitle?: string;
  multipleIngredientsButtonTitle?: string;
  onRequestClose: () => void;
  onChooseNewIngredientPress?: (groupId: string) => void;
};

type CardProps = {
  ingredient: NonNullable<CustomizationMachineTypes.Modification['ingredient']>;
};
